import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { KsgButton, KsgIcon } from '@ksg/ui';
import { TourService } from './tour.service';

interface BoxStyle {
  top: string;
  left: string;
  width: string;
  height: string;
}

const CARD_WIDTH = 340;

@Component({
  selector: 'app-tour-overlay',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoPipe, KsgButton, KsgIcon],
  host: {
    '(window:resize)': 'locate()',
    '(window:scroll)': 'locate()',
    '(document:keydown)': 'onKeydown($event)',
  },
  template: `
    @if (service.activeTour(); as tour) {
      @if (service.currentStep(); as step) {
        <div class="tour-backdrop" aria-hidden="true"></div>

        @if (highlight(); as box) {
          <div class="tour-highlight" [style.top]="box.top" [style.left]="box.left" [style.width]="box.width" [style.height]="box.height" aria-hidden="true"></div>
        }

        <div
          class="tour-card"
          role="dialog"
          aria-modal="false"
          [attr.aria-labelledby]="'tour-title'"
          [attr.aria-describedby]="'tour-body'"
          [style.top]="cardStyle().top"
          [style.left]="cardStyle().left"
          tabindex="-1"
        >
          <div class="flex items-start justify-between gap-2 mb-2">
            <h2 id="tour-title" class="text-base font-semibold m-0">{{ step.titleKey | transloco }}</h2>
            <button
              type="button"
              class="tour-close"
              (click)="service.stop()"
              [attr.aria-label]="'tour.close' | transloco"
            >
              <ksg-icon name="pi-times" [size]="18" />
            </button>
          </div>

          @if (service.sandboxActive() && step.simulatesSave) {
            <p class="tour-sim-note">{{ 'tour.simulationOnly' | transloco }}</p>
          }

          <p id="tour-body" class="text-sm m-0 mb-3">{{ step.bodyKey | transloco }}</p>

          <div class="flex items-center justify-between gap-2">
            <span class="text-xs text-500" aria-live="polite">
              {{ service.stepIndex() + 1 }} / {{ service.totalSteps() }}
            </span>
            <div class="flex gap-2">
              @if (!service.isFirst()) {
                <ksg-button variant="text" size="sm" (click)="service.prev()">{{ 'tour.prev' | transloco }}</ksg-button>
              }
              @if (service.isLast()) {
                <ksg-button size="sm" (click)="service.next()">{{ 'tour.finish' | transloco }}</ksg-button>
              } @else {
                <ksg-button size="sm" (click)="service.next()">{{ 'tour.next' | transloco }}</ksg-button>
              }
            </div>
          </div>
        </div>
      }
    }
  `,
  styles: [
    `
      :host { position: fixed; inset: 0; z-index: 1200; pointer-events: none; }
      :host ::ng-deep .tour-backdrop { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.25); pointer-events: none; }
      .tour-backdrop { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.25); pointer-events: none; }
      .tour-highlight {
        position: fixed;
        border: 2px solid var(--focus, #6366f1);
        border-radius: 8px;
        box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.35), 0 0 0 9999px rgba(0, 0, 0, 0.001);
        transition: all 0.15s ease;
        pointer-events: none;
      }
      .tour-card {
        position: fixed;
        width: 340px;
        max-width: calc(100vw - 24px);
        background: var(--surface, #fff);
        color: var(--text, #1f2937);
        border: 1px solid var(--border, #e5e7eb);
        border-radius: 10px;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
        padding: 1rem;
        pointer-events: auto;
      }
      .tour-close {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: none;
        cursor: pointer;
        color: var(--text-2, #6b7280);
        padding: 0.25rem;
        border-radius: 6px;
      }
      .tour-close:hover { background: var(--surface-2, #f3f4f6); }
      .tour-sim-note {
        font-size: 0.75rem;
        margin: 0 0 0.5rem;
        padding: 0.35rem 0.5rem;
        border-radius: 6px;
        background: #fef3c7;
        color: #92400e;
      }
    `,
  ],
})
export class TourOverlay {
  readonly service = inject(TourService);

  /** Prostokąt celu (fixed/viewport). Aktualizowany przy zmianie kroku, scrollu, resize. */
  private readonly rect = signal<DOMRect | null>(null);

  readonly highlight = computed<BoxStyle | null>(() => {
    const r = this.rect();
    if (!r) {
      return null;
    }
    return {
      top: `${r.top - 4}px`,
      left: `${r.left - 4}px`,
      width: `${r.width + 8}px`,
      height: `${r.height + 8}px`,
    };
  });

  readonly cardStyle = computed<{ top: string; left: string }>(() => {
    const r = this.rect();
    if (typeof window === 'undefined' || !r) {
      return { top: '50%', left: `calc(50% - ${CARD_WIDTH / 2}px)` };
    }
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const left = Math.min(Math.max(r.left, 12), vw - CARD_WIDTH - 12);
    const below = r.bottom + 12;
    const top = below + 220 > vh ? Math.max(r.top - 232, 12) : below;
    return { top: `${top}px`, left: `${Math.max(left, 12)}px` };
  });

  constructor() {
    effect(() => {
      this.service.currentStep();
      this.service.stepIndex();
      queueMicrotask(() => this.locate());
    });
  }

  locate(): void {
    const step = this.service.currentStep();
    if (typeof document === 'undefined' || !step?.selector) {
      this.rect.set(null);
      return;
    }
    const el = document.querySelector<HTMLElement>(step.selector);
    if (!el) {
      this.rect.set(null);
      return;
    }
    el.scrollIntoView({ block: 'center', inline: 'nearest' });
    this.rect.set(el.getBoundingClientRect());
  }

  onKeydown(event: KeyboardEvent): void {
    if (!this.service.activeTour()) {
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      this.service.stop();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.service.next();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.service.prev();
    }
  }
}
